import { FC, HTMLAttributes } from "react";
import clsx from "clsx";

import { BlocksRenderer } from "@strapi/blocks-react-renderer";

import { IContent, ICriteriaPoints } from "@/api/dtos/shared-component.type";
import TextSection from "@/sections/common/TextSection";

// ------------------------------------------------------------------------------------------------------------

type ICyberSecuritySectionProps = HTMLAttributes<HTMLElement> & {
  heading: string;
  contentData?: IContent;
  phases: ICriteriaPoints[];
};

const CyberSecuritySection: FC<ICyberSecuritySectionProps> = (props) => {
  const { heading, phases, contentData, className, ...other } = props;

  return (
    <section
      className={clsx("dark:py-5 dark:lg:py-8 dark:xl:py-[50px]", className)}
      {...other}
    >
      <div className="py-5 dark:bg-black-800 dark:py-10 lg:py-8 dark:lg:py-16 xl:py-[50px] dark:xl:py-[100px]">
        <div className="container mx-auto">
          <div className="flex flex-col gap-10 lg:flex-row lg:gap-16 xl:gap-[102px]">
            {heading && phases && (
              <div className="w-full max-w-[410px]">
                {heading && (
                  <h3 className="text-h6 font-normal dark:text-white lg:text-h5 xl:text-h4">
                    {heading}
                  </h3>
                )}
                {phases && phases.length > 0 && (
                  <div className="relative mt-5 flex w-full gap-5 border-l-[2px] border-black-900 before:absolute before:bottom-px before:-ml-[7px] before:inline-block before:h-3 before:w-3 before:rotate-45 before:border-b-2 before:border-r-2 before:border-black-900 before:content-[''] dark:border-white dark:before:border-white md:mt-7 md:before:-ml-[9px] md:before:h-4 md:before:w-4 lg:mt-10 xl:mt-12">
                    <div className="h-full w-full pl-5 md:pl-7 lg:pl-10">
                      <ul className="space-y-5 lg:space-y-7">
                        {phases.map(({ key, value }, idx) => (
                          <li
                            key={idx}
                            className="flex items-center gap-4 lg:gap-6"
                          >
                            <span className="block text-base font-normal capitalize dark:text-white">
                              {key}
                            </span>
                            <div
                              className={`w-full bg-gradient-to-r from-primary-900 to-white p-px`}
                              style={{ maxWidth: `${330 - 15 * idx}px` }}
                            >
                              <div className="bg-black-900">
                                <span className="block w-full bg-gradient-to-r from-primary-900 to-primary-900/10 p-2 px-5 text-base font-normal text-white dark:text-white lg:px-7">
                                  {value}
                                </span>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )}

            {contentData && (
              <div className="w-full">
                <TextSection
                  heading={contentData.heading}
                  description={
                    contentData.description ? (
                      <BlocksRenderer content={contentData.description} />
                    ) : undefined
                  }
                  buttons={contentData?.button?.map((button) => ({
                    ...button,
                    text: button.title,
                    link: button.href,
                    id: button.btn_id,
                  }))}
                  headingClass="dark:text-white"
                  textWithImageRichTextStyle=""
                  textSectionClass="!p-0 dark:text-black-200 dark:prose-strong:text-white dark:prose-strong:text-base"
                  className="!p-0"
                  innerContainerClass="dark:!bg-black-800"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CyberSecuritySection;
